import { useEffect } from 'react';
import { UserPreferences } from '../types/auth.types';
import { darkenColor, lightenColor, hexToRgbString } from '../utils/color.utils';
import { storeDarkMode } from './theme.storage';
import { colorsForMode, readThemeColorStore } from './themeColors.resolve';
import { LIGHT_DEFAULT_COLORS } from '../pages/Preferences/preferences.palettes';

interface UseThemeColorsProps {
  preferences?: UserPreferences | null;
}

export const applyColorTheme = (type: 'primary' | 'secondary', color: string) => {
  const root = document.documentElement;

  root.style.setProperty(`--color-${type}`, color);
  root.style.setProperty(`--color-${type}-rgb`, hexToRgbString(color));
  root.style.setProperty(`--color-${type}-hover`, darkenColor(color, 15));
  root.style.setProperty(`--color-${type}-active`, darkenColor(color, 25));
  root.style.setProperty(`--color-${type}-light`, lightenColor(color, 30));
};

export const applyDarkMode = (enabled: boolean) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', enabled ? 'dark' : 'light');
  root.style.colorScheme = enabled ? 'dark' : 'light';
  storeDarkMode(enabled);
};

export const applyThemePair = (primary: string, secondary: string) => {
  applyColorTheme('primary', primary);
  applyColorTheme('secondary', secondary);
};

export const resetToDefaultColors = () => {
  applyThemePair(LIGHT_DEFAULT_COLORS.primary, LIGHT_DEFAULT_COLORS.secondary);
};

export const useThemeColors = ({ preferences }: UseThemeColorsProps) => {
  useEffect(() => {
    if (!preferences) return;

    const dark = preferences.dark_mode ?? false;
    const colors = colorsForMode(
      dark,
      preferences.primary_color,
      preferences.secondary_color,
      readThemeColorStore(preferences.ui_preferences)
    );
    applyThemePair(colors.primary, colors.secondary);
    applyDarkMode(dark);
  }, [preferences]);
};
